import React from 'react';
import { useAuditEvents } from '@/hook/useAuditEvents';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';

const mono = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

function copy(text) {
	if (!text) return;
	navigator.clipboard?.writeText(String(text));
}

function toLocal(ts) {
	try {
		const d = new Date(ts);
		if (Number.isNaN(d.getTime())) return '—';
		return d.toLocaleString();
	} catch {
		return '—';
	}
}
function toUTC(ts) {
	try {
		const d = new Date(ts);
		if (Number.isNaN(d.getTime())) return '—';
		return d.toISOString().replace('T', ' ').replace('Z', ' UTC');
	} catch {
		return '—';
	}
}
function msHuman(n) {
	const ms = Number(n);
	if (!Number.isFinite(ms) || ms < 0) return '—';
	if (ms < 1000) return `${ms} ms`;
	const s = ms / 1000;
	if (s < 60) return `${s.toFixed(s < 10 ? 2 : 1)} s`;
	const m = Math.floor(s / 60);
	const rem = s % 60;
	return `${m}m ${rem.toFixed(0)}s`;
}

function statusColor(status) {
	const s = String(status || '').toLowerCase();
	if (['ok', 'success', 'succeeded', 'completed'].includes(s)) return 'success';
	if (['error', 'failed', 'failure'].includes(s)) return 'error';
	if (['retry', 'warning', 'partial'].includes(s)) return 'warning';
	return 'default';
}

function eventColor(event) {
	const e = String(event || '').toLowerCase();
	if (['claimed', 'started', 'queued'].includes(e)) return 'info';
	if (['success', 'finalized', 'completed', 'published'].includes(e)) return 'success';
	if (['failed', 'error', 'timeout'].includes(e)) return 'error';
	return 'default';
}

export function AuditTable() {
	// DataGrid is 0-based; API is 1-based
	const [paginationModel, setPaginationModel] = React.useState({
		page: 0,
		pageSize: 25
	});

	const { data, loading, error } = useAuditEvents({
		page: paginationModel.page + 1,
		limit: paginationModel.pageSize
	});

	const rows = React.useMemo(() => {
		const items = data?.items ?? [];
		return items.map(r => ({ id: r._id, ...r }));
	}, [data]);

	const columns = React.useMemo(
		() => [
			{
				field: 'createdAt',
				headerName: 'Time',
				minWidth: 200,
				flex: 1.2,
				sortable: false,
				renderHeader: p => (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<AccessTimeIcon fontSize="small" />
						<span>{p.colDef.headerName}</span>
					</Box>
				),
				renderCell: params => {
					const utc = toUTC(params.value);
					const local = toLocal(params.value);
					return (
						<Tooltip title={`UTC: ${utc}`}>
							<Typography variant="body2">{local}</Typography>
						</Tooltip>
					);
				}
			},
			{
				field: 'event',
				headerName: 'Event',
				minWidth: 140,
				flex: 0.8,
				renderCell: params => (
					<Chip size="small" label={params.value || '—'} color={eventColor(params.value)} variant="outlined" />
				)
			},
			{
				field: 'status',
				headerName: 'Status',
				minWidth: 120,
				flex: 0.6,
				renderCell: params => (
					<Chip size="small" label={params.value || '—'} color={statusColor(params.value)} variant="outlined" />
				)
			},
			{
				field: 'fieldType',
				headerName: 'Field',
				minWidth: 120,
				flex: 0.7,
				renderCell: params =>
					params.value ? (
						<Chip size="small" label={params.value} />
					) : (
						<Typography variant="body2" color="text.secondary">
							—
						</Typography>
					)
			},
			{
				field: 'workerId',
				headerName: 'Worker',
				minWidth: 170,
				flex: 0.9,
				renderCell: params => {
					const text = params.value || '—';
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
							<Tooltip title={text}>
								<Typography
									variant="body2"
									sx={{
										fontFamily: mono,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap'
									}}
								>
									{text}
								</Typography>
							</Tooltip>
							{params.value && (
								<Tooltip title="Copy worker id">
									<IconButton size="small" onClick={() => copy(params.value)}>
										<ContentCopyIcon fontSize="inherit" />
									</IconButton>
								</Tooltip>
							)}
						</Box>
					);
				}
			},
			{
				field: 'jobId',
				headerName: 'Job ID',
				minWidth: 180,
				flex: 1,
				renderCell: params => {
					const t = params.value || '—';
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
							<Tooltip title={t}>
								<Typography
									variant="body2"
									sx={{ fontFamily: mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
								>
									{t}
								</Typography>
							</Tooltip>
							{params.value && (
								<Tooltip title="Copy job id">
									<IconButton size="small" onClick={() => copy(params.value)}>
										<ContentCopyIcon fontSize="inherit" />
									</IconButton>
								</Tooltip>
							)}
						</Box>
					);
				}
			},
			{
				field: 'itemId',
				headerName: 'Item ID',
				minWidth: 180,
				flex: 1,
				renderCell: params => {
					const t = params.value || '—';
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
							<Tooltip title={t}>
								<Typography
									variant="body2"
									sx={{ fontFamily: mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
								>
									{t}
								</Typography>
							</Tooltip>
							{params.value && (
								<Tooltip title="Copy item id">
									<IconButton size="small" onClick={() => copy(params.value)}>
										<ContentCopyIcon fontSize="inherit" />
									</IconButton>
								</Tooltip>
							)}
						</Box>
					);
				}
			},
			{
				field: 'durationMs',
				headerName: 'Duration',
				minWidth: 110,
				flex: 0.6,
				valueGetter: p => p?.row?.durationMs,
				renderCell: p => <Typography variant="body2">{msHuman(p?.row?.durationMs)}</Typography>
			},
			{
				field: 'attempts',
				headerName: 'Attempts',
				type: 'number',
				minWidth: 100,
				flex: 0.5
			},
			{
				field: 'message',
				headerName: 'Message',
				minWidth: 260,
				flex: 2,
				valueGetter: p => p?.row?.message || p?.row?.error || '—',
				renderCell: p => {
					const text = p?.row?.message || p?.row?.error || '—';
					return (
						<Tooltip title={text}>
							<Typography
								variant="body2"
								sx={{
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									display: '-webkit-box',
									WebkitLineClamp: 2,
									WebkitBoxOrient: 'vertical',
									lineHeight: 1.25
								}}
							>
								{text}
							</Typography>
						</Tooltip>
					);
				}
			},
			{
				field: 'context',
				headerName: 'Context',
				minWidth: 110,
				flex: 0.6,
				sortable: false,
				renderHeader: p => (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<ArticleOutlinedIcon fontSize="small" />
						<span>{p.colDef.headerName}</span>
					</Box>
				),
				renderCell: p => {
					const ctx = p?.row?.context;
					const preview = ctx ? JSON.stringify(ctx, null, 2) : 'No context';
					return (
						<Tooltip title={<pre style={{ margin: 0 }}>{preview}</pre>}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
								<InfoOutlinedIcon fontSize="small" />
								<Typography variant="body2" color="text.secondary">
									{ctx ? 'View' : '—'}
								</Typography>
							</Box>
						</Tooltip>
					);
				}
			},
			{
				field: 'scheduleTimeUTC',
				headerName: 'Scheduled (UTC)',
				minWidth: 190,
				flex: 1,
				renderCell: p => (
					<Typography variant="body2" sx={{ fontFamily: mono }}>
						{toUTC(p.value)}
					</Typography>
				)
			}
		],
		[]
	);

	if (error) {
		return (
			<Box
				sx={{
					p: 2,
					borderRadius: 2,
					bgcolor: 'error.50',
					border: 1,
					borderColor: 'error.200',
					color: 'error.main'
				}}
			>
				Failed to load
			</Box>
		);
	}

	return (
		<Box sx={{ width: '100%' }}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'baseline',
					justifyContent: 'space-between',
					mb: 1,
					gap: 2
				}}
			>
				<Typography variant="subtitle2" color="text.secondary">
					Total: {data?.total ?? '—'} • Page {data?.page ?? paginationModel.page + 1}/ {data?.pages ?? '—'}
				</Typography>
			</Box>

			<Box
				sx={{
					height: 500,
					width: '100%',
					'& .MuiDataGrid-columnHeaders': { position: 'sticky', top: 0 },
					'& .MuiDataGrid-cell': { alignItems: 'start' }
				}}
			>
				<DataGrid
					rows={rows}
					columns={columns}
					loading={loading}
					disableRowSelectionOnClick
					density="compact"
					// server-side pagination
					paginationMode="server"
					rowCount={data?.total || 0}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					pageSizeOptions={[10, 25, 50, 100]}
					// toolbar with quick filter + export
					slots={{ toolbar: GridToolbar }}
					slotProps={{
						toolbar: {
							showQuickFilter: true,
							quickFilterProps: { debounceMs: 500 },
							printOptions: { disableToolbarButton: true }
						}
					}}
					sx={{
						borderRadius: 2,
						borderColor: 'divider',
						'& .MuiDataGrid-virtualScrollerRenderZone': {
							'& .MuiDataGrid-row': {
								borderBottom: '1px solid',
								borderColor: 'divider'
							}
						}
					}}
				/>
			</Box>
		</Box>
	);
}
